import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Upload, X, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Vehicle } from "@/hooks/useVehicles";
import { MaintenanceTask } from "@/hooks/useMaintenance";

const formSchema = z.object({
  task_name: z.string().min(1, "Task name is required"),
  due_date: z.string().optional(),
  due_at_mileage: z.coerce.number().optional(),
  description: z.string().optional(),
});

export type MaintenanceTaskFormValues = z.infer<typeof formSchema>;

interface MaintenanceTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: MaintenanceTaskFormValues, attachments: string[]) => void;
  vehicle: Vehicle;
  isLoading?: boolean;
  uploadAttachment: (file: File) => Promise<string>;
  task?: MaintenanceTask | null;
  mode?: "add" | "edit";
}

export const MaintenanceTaskDialog = ({
  open,
  onOpenChange,
  onSubmit,
  vehicle,
  isLoading,
  uploadAttachment,
  task,
  mode = "add",
}: MaintenanceTaskDialogProps) => {
  const [attachments, setAttachments] = useState<string[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<MaintenanceTaskFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      task_name: "",
      due_date: "",
      due_at_mileage: undefined,
      description: "",
    },
  });

  // Reset form when task changes (for edit mode)
  useEffect(() => {
    if (open && task && mode === "edit") {
      form.reset({
        task_name: task.task_name,
        due_date: task.due_date || "",
        due_at_mileage: task.due_at_mileage || undefined,
        description: task.description || "",
      });
      setAttachments(task.attachments || []);
    } else if (open && mode === "add") {
      form.reset({
        task_name: "",
        due_date: "",
        due_at_mileage: undefined,
        description: "",
      });
      setAttachments([]);
    }
  }, [open, task, mode, form]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingFiles(true);
    try {
      const uploadPromises = Array.from(files).map(uploadAttachment);
      const urls = await Promise.all(uploadPromises);
      setAttachments((prev) => [...prev, ...urls]);
    } catch (error) {
      console.error("Failed to upload files:", error);
    } finally {
      setUploadingFiles(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (values: MaintenanceTaskFormValues) => {
    onSubmit(values, attachments);
    form.reset();
    setAttachments([]);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset();
      setAttachments([]);
    }
    onOpenChange(newOpen);
  };

  const isEditing = mode === "edit";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit" : "Add"} Maintenance Task for {vehicle.name}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="task_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Task Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Oil Change" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="due_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="due_at_mileage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due at Mileage</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="e.g., 50000" 
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Add any notes or details..." 
                      rows={3}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <FormLabel>Attachments</FormLabel>
              <div className="flex flex-wrap gap-2">
                {attachments.map((url, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-1 bg-muted px-2 py-1 rounded text-sm"
                  >
                    <FileText className="h-3 w-3" />
                    <span className="max-w-[100px] truncate">
                      {url.split('/').pop()}
                    </span>
                    <button 
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFiles}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploadingFiles ? "Uploading..." : "Upload Files"}
              </Button>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading || uploadingFiles}>
                {isLoading ? (isEditing ? "Saving..." : "Adding...") : (isEditing ? "Save Changes" : "Add Task")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
